import React, { useState } from "react"
import {
  sectionCardStyle,
  inputStyle,
  buttonStyle,
  primaryButtonStyle,
} from "./ClinicalHistoryStyles"

function getTimelineVisual(templateCode) {
  const key = String(templateCode || "").toUpperCase()

  if (key === "BASE_GENERAL") {
    return {
      icon: "📋",
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    }
  }

  if (key === "QR_PREINTAKE") {
    return {
      icon: "📱",
      bg: "#eef2ff",
      border: "#c7d2fe",
      color: "#4338ca",
    }
  }

  if (key === "INITIAL_DIAGNOSIS") {
    return {
      icon: "🔎",
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    }
  }

  if (key === "ENDO") {
    return {
      icon: "🦷",
      bg: "#fff7ed",
      border: "#fed7aa",
      color: "#c2410c",
    }
  }

  if (key === "PERIO") {
    return {
      icon: "🪥",
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    }
  }

  if (key === "SURGERY") {
    return {
      icon: "✂️",
      bg: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b",
    }
  }

  if (key === "ORTHO") {
    return {
      icon: "😁",
      bg: "#faf5ff",
      border: "#e9d5ff",
      color: "#7e22ce",
    }
  }

  if (key === "PEDIATRIC") {
    return {
      icon: "🧒",
      bg: "#ecfeff",
      border: "#a5f3fc",
      color: "#0f766e",
    }
  }

  return {
    icon: "🩺",
    bg: "#f8fafc",
    border: "#e2e8f0",
    color: "#334155",
  }
}

export function CheckboxGroup({
  options,
  values,
  onChange,
  columns = 3,
  disabled = false,
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 10,
      }}
    >
      {options.map((option) => (
        <label
          key={option.value}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            color: "#334155",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.75 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={values.includes(option.value)}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...values, option.value])
              } else {
                onChange(values.filter((item) => item !== option.value))
              }
            }}
          />
          {option.label}
        </label>
      ))}
    </div>
  )
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  inline = true,
  disabled = false,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: inline ? 18 : 10,
        alignItems: "center",
      }}
    >
      {options.map((option) => (
        <label
          key={option.value}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            color: "#334155",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.75 : 1,
          }}
        >
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  )
}

export function HeaderCountBadge({ text, tone = "neutral" }) {
  const visual =
    tone === "info"
      ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
      : tone === "success"
        ? { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
        : tone === "warning"
          ? { bg: "#fff7ed", border: "#fed7aa", color: "#c2410c" }
          : { bg: "#f8fafc", border: "#e2e8f0", color: "#334155" }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 11,
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </div>
  )
}

export function SectionCard({
  title,
  subtitle,
  children,
  defaultOpen = true,
  badgeText = "",
  badgeTone = "neutral",
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div style={sectionCardStyle}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
          marginBottom: open ? 14 : 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "center",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                {title}
              </div>

              {badgeText ? (
                <HeaderCountBadge text={badgeText} tone={badgeTone} />
              ) : null}
            </div>

            {subtitle ? (
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                {subtitle}
              </div>
            ) : null}
          </div>

          <div
            style={{
              fontSize: 18,
              color: "#64748b",
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {open ? "−" : "+"}
          </div>
        </div>
      </button>

      {open ? children : null}
    </div>
  )
}

export function HubSubsection({
  title,
  subtitle,
  badgeText = "",
  badgeTone = "neutral",
  children,
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>

            {badgeText ? (
              <HeaderCountBadge text={badgeText} tone={badgeTone} />
            ) : null}
          </div>

          {subtitle ? (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                marginTop: 4,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  )
}

export function Field({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gap: 8,
        alignContent: "start",
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: "#475569",
          fontWeight: 900,
          letterSpacing: 0.6,
          textTransform: "uppercase",
        }}
      >
        {label}
      </label>

      <div
        style={{
          display: "grid",
          gap: 6,
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function MetricPill({ label, value, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
        : tone === "success"
          ? { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
          : tone === "warning"
            ? { bg: "#fff7ed", border: "#fed7aa", color: "#c2410c" }
            : { bg: "#f8fafc", border: "#e2e8f0", color: "#334155" }

  return (
    <div
      style={{
        padding: "8px 12px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: "nowrap",
      }}
    >
      {label}: {value}
    </div>
  )
}

export function StatusDot({ tone = "neutral" }) {
  const color =
    tone === "danger"
      ? "#dc2626"
      : tone === "info"
        ? "#2563eb"
        : tone === "success"
          ? "#16a34a"
          : tone === "warning"
            ? "#ea580c"
            : "#64748b"

  return (
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
        display: "inline-block",
      }}
    />
  )
}

export function LauncherPatientCard({ patient, onOpen }) {
  const fullName = `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
  const lastHistory =
    patient.lastClinicalHistoryAt
      ? new Date(patient.lastClinicalHistoryAt).toLocaleDateString("es-MX")
      : null

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        background: "#ffffff",
        padding: 14,
        boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
        display: "grid",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
            {fullName || "Paciente"}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
            {[patient.phone, patient.email].filter(Boolean).join(" · ") || "Sin datos de contacto"}
          </div>
        </div>

        <button
          onClick={onOpen}
          style={{
            ...buttonStyle,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            whiteSpace: "nowrap",
          }}
        >
          Abrir
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <MetricPill
          label="Nacimiento"
          value={patient.birthDate ? String(patient.birthDate).slice(0, 10) : "—"}
        />
        <MetricPill
          label="Historia base"
          value={lastHistory || "Sin registro"}
          tone={lastHistory ? "info" : "neutral"}
        />
      </div>
    </div>
  )
}

export function CompactHistoryCard({
  title,
  subtitle,
  summary,
  updatedAt,
  statusLabel,
  statusTone,
  sourceLabel,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
}) {
  const tone =
    statusTone === "success"
      ? { bg: "#f0fdf4", border: "#bbf7d0", accent: "#166534" }
      : statusTone === "warning"
        ? { bg: "#fff7ed", border: "#fed7aa", accent: "#c2410c" }
        : statusTone === "info"
          ? { bg: "#eff6ff", border: "#bfdbfe", accent: "#1d4ed8" }
          : statusTone === "danger"
            ? { bg: "#fef2f2", border: "#fecaca", accent: "#991b1b" }
            : { bg: "#ffffff", border: "#e2e8f0", accent: "#334155" }

  return (
    <div
      style={{
        border: `1px solid ${tone.border}`,
        borderRadius: 14,
        background: tone.bg,
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) auto",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 8px",
                borderRadius: 999,
                background: "#ffffff",
                border: `1px solid ${tone.border}`,
                color: tone.accent,
                fontSize: 11,
                fontWeight: 900,
                whiteSpace: "nowrap",
              }}
            >
              <StatusDot tone={statusTone} />
              {statusLabel}
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginTop: 6,
              fontWeight: 700,
            }}
          >
            {subtitle}
          </div>
        </div>

        <div
          style={{
            padding: "4px 8px",
            borderRadius: 999,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#475569",
            fontSize: 11,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {sourceLabel}
        </div>
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#475569",
          lineHeight: 1.5,
        }}
      >
        {summary || "Sin resumen clínico."}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            fontWeight: 700,
          }}
        >
          Actualizado: {updatedAt}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {secondaryLabel ? (
            <button onClick={onSecondary} style={buttonStyle}>
              {secondaryLabel}
            </button>
          ) : null}

          {primaryLabel ? (
            <button
              onClick={onPrimary}
              style={{
                ...buttonStyle,
                background: "#ffffff",
                border: `1px solid ${tone.border}`,
                color: tone.accent,
              }}
            >
              {primaryLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function SpecialtyLauncherCard({
  title,
  description,
  updatedAt,
  statusLabel,
  statusTone,
  actionLabel,
  onAction,
}) {
  const tone =
    statusTone === "success"
      ? { bg: "#f0fdf4", border: "#bbf7d0", accent: "#166534" }
      : statusTone === "warning"
        ? { bg: "#fff7ed", border: "#fed7aa", accent: "#c2410c" }
        : statusTone === "info"
          ? { bg: "#eff6ff", border: "#bfdbfe", accent: "#1d4ed8" }
          : { bg: "#ffffff", border: "#e2e8f0", accent: "#334155" }

  return (
    <div
      style={{
        border: `1px solid ${tone.border}`,
        borderRadius: 14,
        background: tone.bg,
        padding: 14,
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {title}
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px",
              borderRadius: 999,
              background: "#ffffff",
              border: `1px solid ${tone.border}`,
              color: tone.accent,
              fontSize: 11,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            <StatusDot tone={statusTone} />
            {statusLabel}
          </div>
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#475569",
            marginTop: 6,
            lineHeight: 1.45,
          }}
        >
          {description}
        </div>

        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            marginTop: 8,
            fontWeight: 700,
          }}
        >
          Último registro: {updatedAt}
        </div>
      </div>

      <div>
        <button
          onClick={onAction}
          style={{
            ...buttonStyle,
            background: "#ffffff",
            border: `1px solid ${tone.border}`,
            color: tone.accent,
            whiteSpace: "nowrap",
          }}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  )
}

export function TimelineItem({
  templateCode,
  title,
  description,
  timestamp,
  statusLabel,
  statusTone,
  actionLabel,
  onAction,
}) {
  const visual = getTimelineVisual(templateCode)

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "44px minmax(0,1fr)",
        gap: 12,
        alignItems: "start",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: 100,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: visual.bg,
            border: `1px solid ${visual.border}`,
            color: visual.color,
            display: "grid",
            placeItems: "center",
            fontSize: 18,
          }}
        >
          {visual.icon}
        </div>

        <div
          style={{
            width: 2,
            flex: 1,
            background: "#e2e8f0",
            marginTop: 8,
            borderRadius: 999,
          }}
        />
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          background: "#ffffff",
          padding: 14,
          display: "grid",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
              {title}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4, lineHeight: 1.45 }}>
              {description}
            </div>
          </div>

          <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
            <MetricPill label="Fecha" value={timestamp} tone={statusTone} />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <StatusDot tone={statusTone} />
              <span style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {actionLabel ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={onAction} style={buttonStyle}>
              {actionLabel}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function ClinicalQrToolsCard({
  qrLoading,
  qrGenerating,
  qrInfo,
  qrImageUrl,
  qrImageLoading,
  isReadOnly,
  readOnlyReason,
  onGenerate,
  onCopy,
  onOpen,
  onDownload,
  onPrint,
  onUpgrade,
}) {
  const isEnabled = qrInfo?.isEnabled ?? false
  const hasActiveLink = qrInfo?.hasActiveLink ?? false
  const publicUrl = qrInfo?.link?.publicUrl || ""

  return (
    <div
      style={{
        border: "1px solid #dbeafe",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) auto",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            QR fijo de la clínica
          </div>

          <div
            style={{
              fontSize: 13,
              color: "#64748b",
              marginTop: 6,
              lineHeight: 1.5,
              maxWidth: 760,
            }}
          >
            Recurso administrativo para recepción, WhatsApp o cartel impreso. No
            necesita protagonismo: solo debe estar disponible cuando haga falta.
          </div>
        </div>

        <MetricPill
          label="Estado"
          value={
            !isEnabled
              ? "Disponible en Plus"
              : hasActiveLink
                ? "Disponible"
                : "Sin generar"
          }
          tone={!isEnabled ? "warning" : hasActiveLink ? "success" : "neutral"}
        />
      </div>

      {qrLoading ? (
        <div style={{ color: "#64748b", fontSize: 13 }}>
          Cargando QR...
        </div>
      ) : null}

      {!isEnabled ? (
        <div
          style={{
            border: "1px solid #fed7aa",
            background: "#fff7ed",
            color: "#9a3412",
            borderRadius: 12,
            padding: 12,
            fontSize: 13,
            lineHeight: 1.5,
            fontWeight: 700,
          }}
        >
          El pre-registro por QR forma parte del ecosistema Plus.
          <div style={{ marginTop: 10 }}>
            <button onClick={onUpgrade} style={buttonStyle}>
              Mejorar plan
            </button>
          </div>
        </div>
      ) : (
        <>
          {hasActiveLink && publicUrl ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "140px minmax(0,1fr)",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  border: "1px solid #dbeafe",
                  background: "#ffffff",
                  borderRadius: 14,
                  padding: 10,
                  display: "grid",
                  placeItems: "center",
                  minHeight: 140,
                }}
              >
                {qrImageLoading ? (
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Generando...
                  </div>
                ) : qrImageUrl ? (
                  <img
                    src={qrImageUrl}
                    alt="QR clínico"
                    style={{
                      width: "100%",
                      maxWidth: 118,
                      height: "auto",
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Sin imagen QR.
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <div
                  style={{
                    border: "1px solid #dbeafe",
                    background: "#ffffff",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#1d4ed8",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Enlace público activo
                  </div>

                  <input
                    readOnly
                    value={publicUrl}
                    style={{
                      ...inputStyle,
                      fontSize: 12,
                      background: "#f8fafc",
                    }}
                  />

                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      lineHeight: 1.45,
                    }}
                  >
                    Este enlace es fijo por clínica. Solo reemplázalo cuando
                    realmente quieras invalidar el anterior.
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={onOpen}
                    style={primaryButtonStyle}
                  >
                    Abrir formulario
                  </button>

                  <button onClick={onCopy} style={buttonStyle}>
                    Copiar enlace
                  </button>

                  <button onClick={onDownload} style={buttonStyle}>
                    Descargar QR
                  </button>

                  <button onClick={onPrint} style={buttonStyle}>
                    Imprimir cartel
                  </button>

                  <button
                    onClick={onGenerate}
                    disabled={qrGenerating || isReadOnly}
                    style={{
                      ...buttonStyle,
                      cursor: qrGenerating || isReadOnly ? "not-allowed" : "pointer",
                      opacity: qrGenerating || isReadOnly ? 0.7 : 1,
                    }}
                  >
                    {qrGenerating ? "Reemplazando..." : "Reemplazar enlace"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                border: "1px dashed #bfdbfe",
                background: "#ffffff",
                borderRadius: 12,
                padding: 12,
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.5,
                display: "grid",
                gap: 12,
              }}
            >
              <div>
                Aún no hay un QR generado para esta clínica.
              </div>

              <div>
                <button
                  onClick={onGenerate}
                  disabled={qrGenerating || isReadOnly}
                  style={{
                    ...primaryButtonStyle,
                    cursor: qrGenerating || isReadOnly ? "not-allowed" : "pointer",
                    opacity: qrGenerating || isReadOnly ? 0.7 : 1,
                  }}
                >
                  {qrGenerating ? "Generando..." : "Generar QR"}
                </button>
              </div>
            </div>
          )}

          {isReadOnly ? (
            <div
              style={{
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#991b1b",
                borderRadius: 12,
                padding: 12,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              🔒 {readOnlyReason}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}